import axios from 'axios';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { User, UserToCreate } from 'src/types/user.types';
import {
  clearOnboardingData,
  getAllOnboardingData,
} from 'src/utils/onboardingStorage';
import { queryKeys } from 'src/queries/keys';

export const useGetUsers = (options?: { enabled?: boolean }) => {
  return useQuery<User[]>({
    queryKey: queryKeys.users(),
    refetchOnMount: 'always',
    enabled: options?.enabled,
  });
};

export const useCreateUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (user: Partial<UserToCreate>): Promise<User> => {
      const { data } = await axios.post('/api/users', user);
      return data;
    },
    onSuccess: async () => {
      await queryClient.refetchQueries(queryKeys.users());
    },
  });
};

export const useUpdateUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (user: Partial<User>): Promise<User> => {
      if (!user.id) {
        throw new Error('User id is required for update');
      }

      const onboardingData = getAllOnboardingData(user.id);

      const mergedData = {
        ...onboardingData,
        ...user,
      };

      const { data } = await axios.put(`/api/users/${user.id}`, mergedData);
      return data;
    },
    onSuccess: async user => {
      clearOnboardingData(user.id);
      await queryClient.refetchQueries(queryKeys.users());
      const current = queryClient.getQueryData<User>(queryKeys.user());
      if (current?.id === user.id) {
        await queryClient.invalidateQueries(queryKeys.user());
      }
    },
  });
};

export const useDeleteUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<void> => {
      const { data } = await axios.delete('/api/user');
      return data;
    },
    onSuccess: () => {
      queryClient.setQueryData(queryKeys.user(), undefined);
      queryClient.removeQueries();
      queryClient.clear();
    },
  });
};

export const useDeleteFamilyUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (userUuid: string): Promise<void> => {
      await axios.delete(`/api/users/${userUuid}`);
    },
    onSuccess: async () => {
      await queryClient.refetchQueries(queryKeys.users());
    },
  });
};
